import type { Metadata } from 'next';
import Link from 'next/link';
import { getHomeData } from '@/lib/api';
import StoryCard from '@/components/ui/StoryCard';
import VideoCard from '@/components/ui/VideoCard';
import SectionHeader from '@/components/ui/SectionHeader';
import Label from '@/components/ui/Label';
import LiveFeed from '@/components/modules/LiveFeed';
import MostRead from '@/components/modules/MostRead';
import HeroSlider from '@/components/modules/HeroSlider';

export const metadata: Metadata = {
  title: 'VERITAS NEWS — Trusted Global Journalism',
  description: 'Breaking news, world news, analysis and opinion from VERITAS NEWS. The most trusted independent news source.',
};

export default async function HomePage() {
  const { hero, latest, breaking, livePost, sections, mostRead, liveUpdates, videos: apiVideos } = await getHomeData();
  
  // Use breaking news for the slider, or hero pinned stories if no breaking news
  const sliderStories = breaking.length > 0 ? breaking : (hero.length > 0 ? hero : latest.slice(0, 3));
  
  const secondaryHero = hero.slice(1, 4).length > 0 ? hero.slice(1, 4) : latest.slice(1, 4);
  const topStories = latest.slice(0, 4);
  
  const worldStories = sections['World'] || [];
  const politicsStories = sections['Politics'] || [];
  const bizStories = sections['Business'] || [];
  const techStories = sections['Technology'] || [];
  const climateStories = sections['Climate'] || [];
  const cultureStories = sections['Culture'] || [];
  const sportStories = sections['Sport'] || [];
  const opinionStories = sections['Opinion'] || [];
  
  const featureStories = latest.filter(s => s.label === 'feature').slice(0, 4);

  return (
    <div className="max-w-[1400px] mx-auto px-5">

      {/* SECTION 1 — HERO GRID */}
      <section className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-px bg-[#D4D4D4] mt-0 mb-0 border-b border-[#D4D4D4]">
        {/* Lead story slider */}
        <div className="bg-white pr-0 lg:pr-6 py-6">
          <HeroSlider stories={sliderStories} />
        </div>
        {/* Secondary cards */}
        <div className="bg-white pl-0 lg:pl-6 divide-y divide-[#D4D4D4]">
          {secondaryHero.map((story) => (
            <Link key={story.id} href={`/article/${story.slug}`} className="block py-4 group hover:opacity-80 transition-opacity cursor-pointer">
              <div 
                className={`${story.imageBg?.startsWith('bg-') ? story.imageBg : 'bg-gray-100'} h-[90px] lg:h-[110px] w-full mb-2 bg-cover bg-center bg-no-repeat`} 
                style={!story.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${story.imageBg})` } : {}}
              />
              <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.1em] uppercase text-[#C8102E]">
                {story.section}
              </span>
              <h3 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[15px] leading-[1.3] text-[#0D0D0D] mt-0.5 group-hover:text-[#C8102E] transition-colors">
                {story.title}
              </h3>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-1">{story.publishedAt}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* SECTION 2 — TOP STORIES */}
      <section className="py-8">
        <SectionHeader title="Top Stories" moreLink="/world" moreLabel="All stories →" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {topStories.map((story) => (
            <StoryCard key={story.id} story={story} size="medium" />
          ))}
        </div>
      </section>

      {/* SECTION 3 — LIVE COVERAGE + MOST READ */}
      <section className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 py-3 pb-10 border-t border-[#D4D4D4]">
        <div className="pt-8">
          <LiveFeed limit={4} initialData={liveUpdates} livePost={livePost} />
        </div>
        <div className="pt-8 lg:border-l lg:border-[#D4D4D4] lg:pl-8">
          <MostRead initialData={mostRead} />
          <div className="mt-8">
            <div className="flex items-baseline justify-between mb-4 pb-2.5 border-b-[2.5px] border-[#0D0D0D]">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Editors&apos; Picks</h3>
            </div>
            {latest.filter(s => s.label === 'analysis').slice(0, 3).map((story) => (
              <Link key={story.id} href={`/article/${story.slug}`} className="block py-3 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group">
                {story.label && <div className="mb-1"><Label type={story.label} /></div>}
                <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[14px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{story.title}</h4>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-1">{story.publishedAt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WORLD */}
      <section className="py-8 border-t border-[#D4D4D4]">
        <SectionHeader title="World" moreLink="/world" moreLabel="More World →" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-7">
          <div>
            {worldStories[0] && (
              <Link href={`/article/${worldStories[0].slug}`} className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-5 mb-7 border-b pb-7 border-[#D4D4D4] group hover:opacity-80 transition-opacity cursor-pointer">
                <div>
                  {worldStories[0].label && <div className="mb-2"><Label type={worldStories[0].label} /></div>}
                  <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] leading-[1.25] mb-2">{worldStories[0].title}</h2>
                  <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] leading-[1.55] text-[#2D2D2D]">{worldStories[0].deck}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-2">{worldStories[0].author} · {worldStories[0].publishedAt}</p>
                </div>
                <div 
                  className={`${worldStories[0].imageBg?.startsWith('bg-') ? worldStories[0].imageBg : 'bg-gray-100'} aspect-video md:aspect-auto bg-cover bg-center bg-no-repeat`} 
                  style={!worldStories[0].imageBg?.startsWith('bg-') ? { backgroundImage: `url(${worldStories[0].imageBg})` } : {}}
                />
              </Link>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {worldStories.slice(1, 4).map(story => <StoryCard key={story.id} story={story} size="medium" />)}
            </div>
          </div>
          <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-7">
            <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">From Our Correspondents</h3>
            </div>
            {worldStories.slice(4, 7).map(story => (
              <Link key={story.id} href={`/article/${story.slug}`} className="block py-3 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group">
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.08em] uppercase text-[#C8102E]">{story.section}</span>
                <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[14px] leading-[1.35] mt-0.5 group-hover:text-[#C8102E] transition-colors">{story.title}</h4>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-1">{story.author} · {story.publishedAt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — POLITICS */}
      <section className="bg-[#F5F4F0] py-9 -mx-5 px-5 border-t border-[#D4D4D4]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeader title="Politics" moreLink="/world" moreLabel="More Politics →" />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-7">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {politicsStories.slice(0,3).map(story => (
                <StoryCard key={story.id} story={story} size="medium" />
              ))}
            </div>
            <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-7">
              <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
                <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Key Votes This Week</h3>
              </div>
              {['EU AI Regulation vote — Brussels', 'UK Infrastructure bill — Commons', 'UN Security Council resolution — New York'].map((vote, i) => (
                <div key={i} className="py-2.5 border-b border-[#D4D4D4]">
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#2D2D2D]">{vote}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-0.5">Thursday, 18 April</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — BUSINESS & MARKETS STRIP */}
      <section className="bg-[#1A1A1A] py-7 px-6 -mx-5 border-t border-[#333]">
        <div className="max-w-[1400px] mx-auto">
          <div className="pb-2.5 border-b-[2.5px] border-white mb-5">
            <h2 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase text-white">Business & Markets</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_2fr] gap-5">
            {[
              { name: 'S&P 500', value: '5,847.23', change: '+0.34%', up: true },
              { name: 'FTSE 100', value: '8,223.45', change: '-0.12%', up: false },
              { name: 'EUR/USD', value: '1.0832', change: '+0.08%', up: true },
            ].map(m => (
              <div key={m.name} className="border border-[#333] p-5">
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#888] uppercase tracking-[0.08em] mb-1">{m.name}</p>
                <p className="font-['Playfair_Display',Georgia,serif] font-bold text-[26px] text-white">{m.value}</p>
                <p className={`font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[13px] ${m.up ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>{m.change}</p>
              </div>
            ))}
            {bizStories[0] && (
              <Link href={`/article/${bizStories[0].slug}`} className="border border-[#333] p-5 group hover:opacity-80 transition-opacity cursor-pointer">
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.1em] uppercase text-[#C8102E] mb-2 block">Business</span>
                <h3 className="font-['Playfair_Display',Georgia,serif] font-bold text-[18px] text-white leading-[1.3] group-hover:text-[#C8102E] transition-colors">{bizStories[0].title}</h3>
                <p className="font-['Source_Serif_4',Georgia,serif] text-[13px] text-[#888] mt-2">{bizStories[0].deck.slice(0, 100)}...</p>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* SECTION 7 — TECHNOLOGY */}
      <section className="py-8 border-t border-[#D4D4D4]">
        <SectionHeader title="Technology" moreLink="/world" moreLabel="More Tech →" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {techStories.slice(0, 2).map(story => <StoryCard key={story.id} story={story} size="large" />)}
          </div>
          <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-7">
            <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Quick Read</h3>
            </div>
            {techStories.map(story => (
              <Link key={story.id} href={`/article/${story.slug}`} className="flex items-start justify-between py-2.5 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group gap-3">
                <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[13px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{story.title}</h4>
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] shrink-0">{story.publishedAt}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — CLIMATE STRIP + GRID */}
      <section className="border-t border-[#D4D4D4]">
        <div className="bg-[#0C7A6E] py-7 px-6 -mx-5 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-center">
          <div>
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.12em] uppercase text-white/70 block mb-2">
              CLIMATE & ENVIRONMENT
            </span>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] text-white leading-[1.25]">
              Earth&apos;s critical systems under strain
            </h2>
          </div>
          <div>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[15px] text-white/85 mb-4 leading-[1.6]">
              From record ocean temperatures to shrinking ice sheets, our climate desk tracks the data and the science behind the headlines.
            </p>
            <Link href="/topic/climate" className="inline-block border border-white text-white font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[12px] px-5 py-2 hover:bg-white hover:text-[#0C7A6E] transition-all">
              Read More →
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 py-8">
          {climateStories.map(story => <StoryCard key={story.id} story={story} size="medium" />)}
        </div>
      </section>

      {/* SECTION 9 — CULTURE */}
      <section className="py-8 border-t border-[#D4D4D4]">
        <SectionHeader title="Culture" moreLink="/world" moreLabel="More Culture →" />
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          {cultureStories[0] && (
            <Link href={`/article/${cultureStories[0].slug}`} className="group hover:opacity-80 transition-opacity cursor-pointer">
              <div 
                className={`${cultureStories[0].imageBg?.startsWith('bg-') ? cultureStories[0].imageBg : 'bg-gray-100'} w-full h-[280px] mb-4 bg-cover bg-center bg-no-repeat`} 
                style={!cultureStories[0].imageBg?.startsWith('bg-') ? { backgroundImage: `url(${cultureStories[0].imageBg})` } : {}}
              />
              <div className="mb-2"><Label type="feature" /></div>
              <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[26px] leading-[1.2] mb-2">{cultureStories[0].title}</h2>
              <p className="font-['Source_Serif_4',Georgia,serif] text-[15px] leading-[1.55] text-[#2D2D2D]">{cultureStories[0].deck}</p>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] mt-2">{cultureStories[0].author} · {cultureStories[0].publishedAt}</p>
            </Link>
          )}
          <div className="divide-y divide-[#D4D4D4]">
            {cultureStories.slice(1, 4).map(story => (
              <div key={story.id} className="py-4">
                <StoryCard story={story} size="compact" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10 — SPORT */}
      <section className="bg-[#F5F4F0] py-8 -mx-5 px-5 border-t border-[#D4D4D4]">
        <SectionHeader title="Sport" moreLink="/world" moreLabel="More Sport →" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {sportStories.map(story => (
            <Link key={story.id} href={`/article/${story.slug}`} className="flex items-start justify-between py-3 px-0 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group gap-4">
              <div>
                {story.label && <div className="mb-1"><Label type={story.label} /></div>}
                <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[15px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{story.title}</h4>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-1">{story.publishedAt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SECTION 11 — VIDEO HIGHLIGHTS */}
      <section className="bg-[#1A1A1A] py-9 -mx-5 px-5 border-t border-[#333]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeader title="Video" moreLink="/video" moreLabel="Watch More →" light />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            {apiVideos.slice(0, 4).map(video => <VideoCard key={video.id} video={video} light />)}
          </div>
          <div className="text-center mt-8">
            <Link href="/video" className="inline-block border-2 border-white/40 text-white font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] px-8 py-3 hover:bg-white hover:text-[#1A1A1A] transition-all">
              Watch More Videos
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 12 — FEATURES & LONG READS */}
      <section className="py-8 border-t border-[#D4D4D4]">
        <SectionHeader title="Features & Long Reads" moreLink="/latest" moreLabel="All Features →" />
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          {featureStories[0] && (
            <Link href={`/article/${featureStories[0].slug}`} className="group hover:opacity-80 transition-opacity cursor-pointer">
              <div 
                className={`${featureStories[0].imageBg?.startsWith('bg-') ? featureStories[0].imageBg : 'bg-gray-100'} w-full h-[320px] mb-4 bg-cover bg-center bg-no-repeat`} 
                style={!featureStories[0].imageBg?.startsWith('bg-') ? { backgroundImage: `url(${featureStories[0].imageBg})` } : {}}
              />
              <div className="mb-2"><Label type="feature" /></div>
              <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[28px] leading-[1.2] mb-2">{featureStories[0].title}</h2>
              <p className="font-['Source_Serif_4',Georgia,serif] text-[15px] leading-[1.55] text-[#2D2D2D] mb-3">{featureStories[0].deck}</p>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{featureStories[0].author} · {featureStories[0].readingTime} min read</p>
            </Link>
          )}
          <div className="divide-y divide-[#D4D4D4]">
            {featureStories.slice(1, 4).map(story => (
              <Link key={story.id} href={`/article/${story.slug}`} className="block py-4 group hover:opacity-75 transition-opacity">
                {story.label && <div className="mb-1.5"><Label type={story.label} /></div>}
                <h3 className="font-['Playfair_Display',Georgia,serif] font-bold text-[17px] leading-[1.3] mb-1 group-hover:text-[#C8102E] transition-colors">{story.title}</h3>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B]">{story.author} · {story.readingTime} min read</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 13 — IN PICTURES */}
      <section className="py-8 border-t border-[#D4D4D4]">
        <SectionHeader title="In Pictures" moreLink="/video" moreLabel="More →" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {['bg-blue-200', 'bg-orange-200', 'bg-teal-200', 'bg-purple-200', 'bg-green-200'].map((bg, i) => (
            <div key={i} className={`${bg} aspect-square relative group cursor-pointer hover:opacity-80 transition-opacity`}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                <p className="text-white font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] leading-tight">
                  {['Geneva negotiations', 'California fires', 'Arctic survey', 'Berlin protests', 'NATO summit'][i]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 14 — OPINION STRIP */}
      <section className="bg-[#F5F4F0] py-9 -mx-5 px-5 border-t border-[#D4D4D4]">
        <SectionHeader title="Opinion & Analysis" moreLink="/opinion" moreLabel="All Opinion →" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 mt-6">
          {opinionStories.map((story) => {
            const author = story.authorDetails;
            if (!author) return null;
            return (
              <Link key={story.id} href={`/article/${story.slug}`} className="block group hover:opacity-80 transition-opacity cursor-pointer border-t-[3px] border-[#1A4A8A] pt-4">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-[52px] h-[52px] circle-avatar ${author.avatarBg} shrink-0 flex items-center justify-center`}>
                    <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[14px] text-[#6B6B6B]">{author.initials}</span>
                  </div>
                  <div>
                    <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[15px] text-[#0D0D0D]">{author.name}</p>
                    <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{author.role}</p>
                  </div>
                </div>
                <div className="mb-3"><Label type="opinion" /></div>
                <h3 className="font-['Playfair_Display',Georgia,serif] font-bold text-[18px] leading-[1.3] mb-2 group-hover:text-[#1A4A8A] transition-colors">{story.title}</h3>
                <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] text-[#2D2D2D] leading-[1.55] line-clamp-2">{story.deck}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SECTION 15 — NEWSLETTER + APP */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[#D4D4D4] -mx-5">
        <div className="py-10 px-8 border border-[#D4D4D4] bg-white">
          <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[26px] mb-2 text-[#0D0D0D]">Stay informed — sign up free</h2>
          <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B] mb-6">Get the day&apos;s most important news delivered to your inbox.</p>
          {[
            { name: 'Morning Briefing', desc: 'Daily at 6am' },
            { name: 'World Dispatch', desc: 'Weekly by Elena Marchetti' },
          ].map((nl) => (
            <div key={nl.name} className="mb-4">
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[14px] mb-0.5">{nl.name}</p>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] mb-2">{nl.desc}</p>
              <div className="flex border border-[1.5px] border-[#0D0D0D] overflow-hidden">
                <input type="email" placeholder="your@email.com" className="flex-1 px-3 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] outline-none bg-transparent" />
                <button className="bg-[#C8102E] text-white px-4 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[12px] hover:bg-[#9E0C23] transition-colors whitespace-nowrap">Subscribe</button>
              </div>
            </div>
          ))}
          <Link href="/newsletters" className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#C8102E] font-semibold hover:underline">
            See all 9 newsletters →
          </Link>
        </div>
        <div className="bg-[#1A1A1A] py-10 px-8 text-white">
          <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] mb-4">Get the Veritas app</h2>
          <ul className="space-y-2 mb-6">
            {['Offline reading — save stories for the commute', 'Breaking alerts — never miss a major story', 'Ad-lite experience — fewer interruptions'].map(f => (
              <li key={f} className="flex items-start gap-2">
                <span className="text-[#C8102E] mt-0.5">•</span>
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] text-white/80">{f}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-3">
            {['App Store', 'Google Play'].map(store => (
              <button key={store} className="border-2 border-white/40 px-5 py-2.5 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] text-white hover:bg-white hover:text-[#1A1A1A] transition-all">
                {store}
              </button>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
